import { useState, useMemo, useCallback } from 'react'
import { BuffetGlobalCartQuery, BuffetItemTypeEnum } from 'graphQL/dinein/generate/operations'
import { useTranslation } from 'react-i18next'
import useLanguage from 'hooks/useLanguage'

type BuffetOrder = BuffetGlobalCartQuery['currentOrder']
interface BuffetItemType {
  name: string
  totalSatang: number
}

const useBuffetOrder = () => {
  const [currentOrder, setCurrentOrder] = useState<BuffetOrder>()
  const { t } = useTranslation()
  const { getPropertyByLang } = useLanguage()

  const getPackageDetail = useCallback(
    (_package) => {
      return `x${_package.quantity} ${t('buffet.globalCartPage.package')} ${getPropertyByLang({
        en: String(currentOrder?.buffetPackage.nameEn),
        th: String(currentOrder?.buffetPackage.nameTh),
      })} ${getPropertyByLang({
        en: _package.buffetItem.labelEn,
        th: _package.buffetItem.labelTh,
      })}`
    },
    [currentOrder, getPropertyByLang, t]
  )

  const buffetPackages: BuffetItemType[] = useMemo(
    () =>
      currentOrder?.buffetItems
        .filter((item) => item.buffetItemType === BuffetItemTypeEnum.Person)
        .map((personPackage) => ({
          name: getPackageDetail(personPackage),
          totalSatang: personPackage.totalSatangs,
        })) ?? [],
    [currentOrder, getPackageDetail]
  )

  const buffetItems: BuffetItemType[] = useMemo(() => {
    const buffetItems: BuffetItemType[] = []
    currentOrder?.buffetItems
      .filter((item) => item.buffetItemType !== BuffetItemTypeEnum.Person && item.totalSatangs > 0)
      .forEach((item) => {
        const name =
          item.buffetItemType === BuffetItemTypeEnum.Overtime
            ? t('buffet.globalCartPage.overTime')
            : item.buffetItemType === BuffetItemTypeEnum.LeftOver
            ? t('buffet.globalCartPage.leftOver')
            : ''
        buffetItems.push({
          name,
          totalSatang: item.totalSatangs,
        })
      })
    return buffetItems
  }, [currentOrder, t])

  const lineItems: BuffetItemType[] = useMemo(() => {
    const lineItems: BuffetItemType[] = []
    currentOrder?.lineItems.forEach((lineItem) => {
      if (lineItem.buffetPackageID !== currentOrder.buffetPackageID) {
        const name = `x${lineItem.quantity} ${getPropertyByLang({
          en: lineItem.product.nameEn,
          th: lineItem.product.nameTh,
        })}`
        lineItems.push({
          name,
          totalSatang: lineItem.totalSatangs,
        })
      }
    })

    return lineItems
  }, [currentOrder, getPropertyByLang])

  return { currentOrder, setCurrentOrder, buffetItems, buffetPackages, lineItems }
}

export default useBuffetOrder
